import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Form, Input, InputNumber, List, Modal, Popconfirm, Typography, message } from 'antd';
import {
    PlusOutlined, DeleteOutlined, FileTextOutlined,
    LinkOutlined, EyeOutlined, UploadOutlined, CopyOutlined,
    PaperClipOutlined, CloseOutlined, SyncOutlined
} from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import { useHomeAssignmentActions } from '_actions/homeAssignment.actions';
import { useClassActions } from '_actions';
import { useClassWrapper } from '_helpers';
import { ROLE_GROUPS, ROLES } from '_helpers/roles';
import { useRecoilValue } from 'recoil';
import { classesAtom } from '_state';
import { SubmissionRoster } from './SubmissionRoster';
import DatePicker from '_helpers/DatePicker';
import dayjs from '_helpers/datetime';
import { useHistory, useLocation } from 'react-router-dom';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

export { HomeAssignmentBoard };

function HomeAssignmentBoard() {
    const classWrapper = useClassWrapper();
    const actions = useHomeAssignmentActions();
    const classes = useRecoilValue(classesAtom);
    const classActions = useClassActions();
    const history = useHistory();
    const location = useLocation();
    const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || 'null'), []);
    const isTeacher = ROLE_GROUPS.STAFF.includes(userData?.role);
    const isStudent = userData?.role === ROLES.STUDENT;
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const linkedAssignmentId = params.get('assignmentId');

    useEffect(() => {
        if (isStudent && classes.length === 0) classActions.getClassList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStudent]);

    const classId = classWrapper.curClass?.class_id;

    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // Link modal state.
    const [linkModal, setLinkModal] = useState({ visible: false, url: '', expiresAt: null, title: '' });
    // Roster modal state.
    const [rosterModal, setRosterModal] = useState({ visible: false, assignment: null });
    // Per-card upload (student) loading flag.
    const [uploadingId, setUploadingId] = useState(null);
    const [filter, setFilter] = useState(params.get('filter') || 'all');

    const load = useCallback(async () => {
        if (!classId) return;
        setLoading(true);
        try {
            const data = await actions.getAssignments(classId);
            setAssignments(Array.isArray(data) ? data : []);
        } catch (e) {
            setAssignments([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!linkedAssignmentId || !isTeacher || !assignments.length) return;
        const found = assignments.find(a => String(a._id || a.id) === String(linkedAssignmentId));
        if (found) setRosterModal({ visible: true, assignment: found });
    }, [linkedAssignmentId, isTeacher, assignments]);

    const filteredAssignments = useMemo(() => assignments.filter(a => matchAssignmentFilter(a, filter, isStudent)), [assignments, filter, isStudent]);
    const setFilterAndUrl = (key) => {
        setFilter(key);
        const q = new URLSearchParams(location.search);
        if (key === 'all') q.delete('filter'); else q.set('filter', key);
        history.replace(`${location.pathname}${q.toString() ? `?${q.toString()}` : ''}`);
    };

    const openCreate = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) { return; }
        setSaving(true);
        try {
            const payload = {
                title: values.title,
                description: values.description || '',
                due_date: values.due_date ? values.due_date.valueOf() : undefined,
                grace_hours: values.grace_hours,
            };
            const res = await actions.createAssignment(classId, payload);
            if (res && res.assignment) {
                message.success('Đã tạo bài tập về nhà');
                setModalVisible(false);
                load();
            } else {
                message.error((res && res.message) || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    };

    const handleDelete = async (a) => {
        try {
            const res = await actions.deleteAssignment(classId, a._id || a.id);
            if (res && (res.status === 'Success' || res.success || res.ok !== false)) {
                message.success('Đã xóa bài tập');
                load();
            } else {
                message.error((res && res.message) || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
    };

    const handleGenerateLink = async (a) => {
        try {
            const res = await actions.generateLink(classId, a._id || a.id);
            if (res && res.url) {
                setLinkModal({ visible: true, url: res.url, expiresAt: res.expiresAt, title: a.title });
            } else {
                message.error((res && res.message) || 'Không tạo được link');
            }
        } catch (e) { message.error('Không tạo được link'); }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(linkModal.url);
            message.success('Đã sao chép link');
        } catch (e) {
            message.warning('Không thể sao chép tự động, vui lòng copy thủ công');
        }
    };

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Bài tập</h1>
                    <div className="att2-sub">{filteredAssignments.length}/{assignments.length} bài tập</div>
                </div>
                {isTeacher &&
                    <div className="att2-tools">
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                            Tạo bài tập
                        </Button>
                    </div>}
            </div>

            <div className="hw-list soft-in-scale">
                <div className="hw-filters">
                    {filterOptions(isStudent).map(o => (
                        <Button
                            key={o.key}
                            size="small"
                            type={filter === o.key ? 'primary' : 'default'}
                            onClick={() => setFilterAndUrl(o.key)}
                        >
                            {o.label}
                        </Button>
                    ))}
                </div>
                <List
                    loading={loading}
                    dataSource={filteredAssignments}
                    locale={{ emptyText: <Empty description="Chưa có bài tập về nhà nào" /> }}
                    renderItem={(a) => (
                        <AssignmentCard
                            key={a._id || a.id}
                            assignment={a}
                            highlighted={linkedAssignmentId && String(a._id || a.id) === String(linkedAssignmentId)}
                            isTeacher={isTeacher}
                            isStudent={isStudent}
                            classId={classId}
                            uploadingId={uploadingId}
                            setUploadingId={setUploadingId}
                            actions={actions}
                            onDelete={handleDelete}
                            onGenerateLink={handleGenerateLink}
                            onViewRoster={(asg) => setRosterModal({ visible: true, assignment: asg })}
                            onUploaded={load}
                        />
                    )}
                />
            </div>

            {/* Create modal */}
            <Modal
                title="Tạo bài tập về nhà"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
                confirmLoading={saving}
                okText="Tạo"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Tiêu đề"
                        rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                    >
                        <Input placeholder="VD: Bài tập Unit 3" />
                    </Form.Item>
                    <Form.Item name="description" label="Mô tả">
                        <TextArea rows={4} placeholder="Yêu cầu, hướng dẫn nộp bài..." />
                    </Form.Item>
                    <Form.Item name="due_date" label="Hạn nộp (tùy chọn)">
                        <DatePicker
                            style={{ width: '100%' }}
                            showTime
                            format="DD/MM/YYYY HH:mm"
                            placeholder="Chọn ngày giờ"
                        />
                    </Form.Item>
                    <Form.Item name="grace_hours" label="Thời gian gia hạn (giờ)">
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="VD: 24" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Link modal (with QR) */}
            <Modal
                title="Link nộp bài"
                visible={linkModal.visible}
                onCancel={() => setLinkModal({ ...linkModal, visible: false })}
                footer={null}
            >
                <p><Text strong>{linkModal.title}</Text></p>
                <p>Học sinh quét mã QR hoặc mở link dưới đây để nộp bài (không cần đăng nhập):</p>
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                    {linkModal.url ? <QRCodeCanvas value={linkModal.url} size={200} includeMargin /> : null}
                </div>
                <Input.Group compact style={{ display: 'flex' }}>
                    <Input value={linkModal.url} readOnly style={{ flex: 1 }} />
                    <Button icon={<CopyOutlined />} onClick={copyLink}>Sao chép</Button>
                </Input.Group>
                {linkModal.expiresAt &&
                    <p style={{ marginTop: 12 }}>
                        <Text type="secondary">
                            Link hết hạn lúc: {dayjs(linkModal.expiresAt).format('DD/MM/YYYY HH:mm')}
                        </Text>
                    </p>}
            </Modal>

            {/* Submission roster modal */}
            <SubmissionRoster
                visible={rosterModal.visible}
                onClose={() => setRosterModal({ visible: false, assignment: null })}
                classId={classId}
                assignment={rosterModal.assignment}
            />
        </div>
    );
}

function filterOptions(isStudent) {
    return isStudent
        ? [
            { key: 'all', label: 'Tất cả' },
            { key: 'due_soon', label: 'Sắp đến hạn' },
            { key: 'overdue', label: 'Quá hạn' },
            { key: 'submitted', label: 'Đã nộp' },
            { key: 'reviewed', label: 'Đã chấm' },
        ]
        : [
            { key: 'all', label: 'Tất cả' },
            { key: 'due_soon', label: 'Sắp đến hạn' },
            { key: 'overdue', label: 'Quá hạn' },
            { key: 'submitted', label: 'Đã nộp' },
            { key: 'awaiting_review', label: 'Chờ chấm' },
            { key: 'reviewed', label: 'Đã chấm' },
        ];
}

function matchAssignmentFilter(a, filter, isStudent) {
    if (filter === 'all') return true;
    const now = dayjs();
    const due = a.due_date ? dayjs(a.due_date) : null;
    const hasSubmission = isStudent
        ? !!(a.my_submission && a.my_submission.files > 0)
        : !!(a.stats && a.stats.submitted > 0);
    const currentReviewed = isStudent
        ? !!(a.my_submission && a.my_submission.review && a.my_submission.review.revision === a.my_submission.submission_revision)
        : !!(a.stats && a.stats.reviewed > 0);
    if (filter === 'due_soon') return !hasSubmission && due && due.isAfter(now) && due.diff(now, 'hour') <= 72;
    if (filter === 'overdue') return !hasSubmission && due && due.isBefore(now);
    if (filter === 'submitted') return hasSubmission;
    if (filter === 'awaiting_review') return !!(a.stats && a.stats.awaiting_review > 0);
    if (filter === 'reviewed') return currentReviewed;
    return true;
}

function AssignmentCard({
    assignment: a, highlighted, isTeacher, isStudent, classId,
    uploadingId, setUploadingId, actions,
    onDelete, onGenerateLink, onViewRoster, onUploaded,
}) {
    const fileInputRef = useRef(null);
    const attachInputRef = useRef(null);
    const id = a._id || a.id;
    const uploading = uploadingId === id;
    const [attaching, setAttaching] = useState(false);

    const handlePickFile = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        setUploadingId(id);
        try {
            const res = await actions.submitFile(classId, id, file);
            if (res && res.ok) {
                message.success('Đã nộp bài');
                onUploaded();
            } else {
                message.error((res && res.message) || 'Nộp bài thất bại');
            }
        } catch (err) {
            message.error('Nộp bài thất bại');
        }
        setUploadingId(null);
    };

    const handlePickAttachment = () => {
        if (attachInputRef.current) attachInputRef.current.click();
    };

    const handleAttachmentChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        setAttaching(true);
        try {
            const res = await actions.addAttachment(classId, id, file);
            if (res && res.ok) {
                message.success('Đã tải tài liệu');
                onUploaded();
            } else {
                message.error((res && res.message) || 'Tải tài liệu thất bại');
            }
        } catch (err) {
            message.error('Tải tài liệu thất bại');
        }
        setAttaching(false);
    };

    const handleDeleteAttachment = async (fileId) => {
        try {
            const res = await actions.deleteAttachment(classId, id, fileId);
            if (res && (res.status === 'Success' || res.success || res.attachments || res.ok !== false)) {
                message.success('Đã xóa tài liệu');
                onUploaded();
            } else {
                message.error((res && res.message) || 'Có lỗi xảy ra');
            }
        } catch (err) {
            message.error('Có lỗi xảy ra');
        }
    };

    const attachments = Array.isArray(a.attachments) ? a.attachments : [];
    // my_submission.files is a COUNT (number) from the API, not an array.
    const myFileCount = (a.my_submission && a.my_submission.files) || 0;
    const hasSubmitted = myFileCount > 0;
    const review = a.my_submission && a.my_submission.review;
    const hasReview = !!(review && (review.grade != null || review.comment || (review.marked_files && review.marked_files.length)));

    return (
        <div className={'hw-row' + (highlighted ? ' hw-row-highlight' : '')}>
            <div className="hw-row-head">
                <span className="hw-row-title"><FileTextOutlined /> {a.title}</span>
                {isTeacher &&
                    <span className="hw-row-actions">
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onViewRoster(a)}>
                            Xem bài nộp
                        </Button>
                        <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => onGenerateLink(a)}>
                            Lấy link nộp bài
                        </Button>
                        <Popconfirm
                            title={`Xóa bài tập "${a.title}"?`}
                            onConfirm={() => onDelete(a)}
                            okText="Xóa" cancelText="Hủy" okType="danger"
                        >
                            <Button type="link" danger size="small" icon={<DeleteOutlined />}>Xóa</Button>
                        </Popconfirm>
                    </span>}
            </div>

            {a.description
                ? <Paragraph className="hw-row-desc">{a.description}</Paragraph>
                : <Text type="secondary" className="hw-row-desc-empty">(Không có mô tả)</Text>}

            <div className="hw-row-tags">
                {a.due_date &&
                    <span className="tag tag-warn">
                        Hạn nộp: {dayjs(a.due_date).format('DD/MM/YYYY HH:mm')}
                    </span>}
                {a.grace_hours ?
                    <span className="tag tag-neutral">Gia hạn: {a.grace_hours} giờ</span> : null}
                {isTeacher && a.stats &&
                    <span className="tag tag-neutral">
                        Nộp {a.stats.submitted}/{a.stats.total} · chờ chấm {a.stats.awaiting_review}
                    </span>}
            </div>

            {/* Teacher reference material — published WITH the assignment, visible to everyone. */}
            {(attachments.length > 0 || isTeacher) &&
                <div className="hw-attach">
                    <Text strong className="hw-attach-title">
                        <PaperClipOutlined /> Tài liệu của giáo viên
                    </Text>
                    {attachments.length > 0
                        ? (
                            <List
                                size="small"
                                dataSource={attachments}
                                split={false}
                                className="hw-attach-list"
                                renderItem={(att) => (
                                    <List.Item className="hw-attach-item">
                                        <a
                                            href={`/api/photos/${att.drive_file_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {att.original_name || 'Tải tệp'}
                                        </a>
                                        {isTeacher &&
                                            <Popconfirm
                                                title={`Xóa tài liệu "${att.original_name || ''}"?`}
                                                onConfirm={() => handleDeleteAttachment(att.drive_file_id)}
                                                okText="Xóa" cancelText="Hủy" okType="danger"
                                            >
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    icon={<CloseOutlined />}
                                                />
                                            </Popconfirm>}
                                    </List.Item>
                                )}
                            />
                        )
                        : <div className="hw-attach-empty"><Text type="secondary">Chưa có tài liệu</Text></div>}

                    {isTeacher &&
                        <div className="hw-attach-upload">
                            <input
                                ref={attachInputRef}
                                type="file"
                                accept="image/*,application/pdf,audio/*"
                                style={{ display: 'none' }}
                                onChange={handleAttachmentChange}
                            />
                            <Button
                                size="small"
                                icon={<UploadOutlined />}
                                loading={attaching}
                                onClick={handlePickAttachment}
                            >
                                Tải tài liệu
                            </Button>
                        </div>}
                </div>}

            {isStudent &&
                <div className="hw-submit">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf,audio/*"
                        capture
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button
                        type={hasSubmitted ? 'default' : 'primary'}
                        icon={hasSubmitted ? <SyncOutlined /> : <UploadOutlined />}
                        loading={uploading}
                        onClick={handlePickFile}
                    >
                        {hasSubmitted ? 'Đổi bài' : 'Nộp bài'}
                    </Button>
                    {hasSubmitted &&
                        <span className="tag tag-ok hw-submit-tag">Đã nộp lần {a.my_submission?.submission_revision || 1}</span>}
                </div>}

            {isStudent && hasReview &&
                <div className="hw-attach" style={{ marginTop: 12 }}>
                    <Text strong className="hw-attach-title"><FileTextOutlined /> Kết quả chấm bài</Text>
                    <div style={{ padding: '4px 0' }}>
                        {review.grade != null &&
                            <div style={{ marginBottom: 6 }}>
                                <Text strong>Điểm: </Text>
                                <span style={{ fontSize: 20, fontWeight: 700, color: '#1668dc' }}>{review.grade}</span>
                                <Text type="secondary"> / 10</Text>
                            </div>}
                        {review.comment &&
                            <div style={{ marginBottom: 6 }}>
                                <Text strong>Nhận xét: </Text>
                                <Text>{review.comment}</Text>
                            </div>}
                        {review.marked_files && review.marked_files.length > 0 &&
                            <div>
                                <Text strong>Bài đã chấm:</Text>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                    {review.marked_files.map((m) => (
                                        <a key={m.drive_file_id} href={`/api/photos/${m.drive_file_id}`} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={`/api/photos/${m.drive_file_id}`}
                                                alt="Bài đã chấm"
                                                style={{ width: 96, height: 96, objectFit: 'cover', border: '1px solid #ddd', borderRadius: 6 }}
                                            />
                                        </a>
                                    ))}
                                </div>
                            </div>}
                    </div>
                </div>}
        </div>
    );
}
